/**
 * API module returned by optimizely.get('recommender');
 *
 *  Recommendations are currently served via DCP; in the future we could have
 *  a separate service that may co-exist with DCP.
 *
 * @module
 */
var _ = require('optimizely-client-fn');

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var PromiseEnv = require('core/lib/promise').Promise;
var xhr = require('core/utils/xhr');

var versions = {
  SELF_CONTAINED_MULTI_COL: 1,
  SEPARATE_CATALOG_SINGLE_COL: 2,
};

var ITEM_METADATA_KEY = 'itemMetadata';
var SINGLE_COLUMN_KEY = 'recos';
var RECO_KEY_PREFIX = 'reco';
var MAX_ITEM_METADATA_TO_FETCH = 20;

/**
 * Reports whether the recommender service version serves multiple columned recos.
 * @param {number} serviceVersion
 * @returns {boolean} Whether the recommender service version serves multiple columned recos.
 * @private
 */
function hasMultipleColumns(serviceVersion:number): boolean {
  return serviceVersion === versions.SELF_CONTAINED_MULTI_COL;
}

/**
 * Reports whether the recommender service version has a catalog.
 * @param {number} serviceVersion
 * @returns {boolean} Whether the recommender service version has a catalog.
 * @private
 */
function hasCatalog(serviceVersion:number): boolean {
  return serviceVersion === versions.SEPARATE_CATALOG_SINGLE_COL;
}

/**
 * Parses recommendations from payload and returns them as a list.
 * NOTE: This helper method exists because we server recommendations using DCP which returns
 * data:
 *  - For single-column format as a JSON encoded object under the key 'recos'.
 *  - For multi-column format as key: value pairs.
 *    The keys are of the form 'reco1', 'reco2', ... and the values are JSON encoded objects.
 * @param {Object} recoPayload
 * @param {number} serviceVersion
 * @returns {Array.<string>} List of ordered recommended items (ids or self-contained).
 * @private
 */
function parsePayload(recoPayload:any, serviceVersion:number): string[] {
  if (!hasMultipleColumns(serviceVersion)) {
    if (SINGLE_COLUMN_KEY in recoPayload) {
      return JSONEnv.parse(recoPayload[SINGLE_COLUMN_KEY]);
    } else {
      Logger.warn('recommender / Expected key "' + SINGLE_COLUMN_KEY + '" not found');
      return [];
    }
  }

  // Service returns multi-column payload.
  // Recommendations are keyed by 'reco(\d+)'; a smaller number indicates higher score.
  var keyRe = new RegExp('^' + RECO_KEY_PREFIX + '(\\d+)$');
  var recommendations = _.reduce(recoPayload, function(recs, value, key) {
    var keyMatch = key.match(keyRe);
    if (keyMatch) {
      var reco = JSONEnv.parse(recoPayload[key]);
      if (!!reco && _.isObject(reco)) {
        recs[keyMatch[1]] = reco;
      }
    }
    return recs;
  }, []);

  // Remove undefined array slots.
  return _.filter(recommendations, function(reco) {
    return !_.isUndefined(reco);
  });
}

/**
 * Returns the URL to fetch recommendations for a specific item.
 * @param {string} [servingUrl] URL to fetch recommendations from.
 * @param {string} targetId
 * @returns {string} The URL to fetch recommendations for item.
 * @private
 */
function generateRecommendationsUrl(servingUrl?:string, targetId?:string): string {
  return [servingUrl, encodeURIComponent(targetId)].join('/');
}

/**
 * Gets the recommender service and recommender entities from the global store.
 * @param GlobalStore
 * @param options
 * @returns {{recommenderService: Object, recommender: Object}}
 * @private
 */
function getEntities(GlobalStore, options): any {
  /**
   * Returns the only object or the object with specified id from list of objects .
   * @param {Object[]} listOfObjects
   * @param {string} id
   * @param {string} objType
   * @returns {Object}
   * @private
   */
  var getOnlyOrObjectWithId = function(listOfObjects:any[], id:string, objType:string): any {
    if (!_.isArray(listOfObjects) || _.isEmpty(listOfObjects)) {
      throw new Error('No ' + objType + 's available');
    }

    var object;
    if (!id) {
      if (listOfObjects.length > 1) {
        throw new Error('No ' + objType + ' id specified when there are more than one available.');
      }
      object = listOfObjects[0];
    } else {
      object = _.find(listOfObjects, {id: id});
      if (!object) {
        throw new Error('No ' + objType + ' found with specified id ' + id);
      }
    }
    return object;
  };

  var availableRecoServices = GlobalStore.getRecommenderServices();
  // Find the recommender service (if specified) or default to only available.
  var recommenderService = getOnlyOrObjectWithId(
    availableRecoServices, options && options.recommenderServiceId, 'recommender service');

  if (!recommenderService.idTagName) {
    recommenderService.idTagName = 'id';
  }

  // Find the recommender (if specified) or default to only available.
  var recommender = getOnlyOrObjectWithId(
    recommenderService.recommenders, options && options.recommenderId, 'recommender');

  return {
    recommenderService: recommenderService,
    recommender: recommender,
  };
}

/**
 * Parses and returns the response data.
 * @param {Object} response
 * @returns {Object} Response data.
 * @private
 */
function getResponseData(response:any): any {
  var payload = JSONEnv.parse(response);
  return payload.data;  // DCP returns attribute data under this field.
}

/**
 * Fetches item metadata from catalog.
 * @param {string} catalogUrl
 * @param {string} itemId
 * @returns {Promise} Promise which resolves with item metadata.
 * @private
 */
function fetchItemMetadataFromCatalog(catalogUrl:string, itemId:string): Promise<any> {
  return xhr.request({
    url: generateRecommendationsUrl(catalogUrl, itemId),
  }).then(function (xhrObj) {
    var data = getResponseData(xhrObj.response);
    var metadata = {} as any;
    if (ITEM_METADATA_KEY in data) {
      metadata = JSONEnv.parse(data[ITEM_METADATA_KEY]);
    }
    return metadata;
  });
}

/**
 * Implementor that fetches recommendations.
 * @param {RecommenderService} recommenderService
 * @param {Recommender} recommender
 * @param {string} targetId
 * @param {Object} options
 * @param {string} [options.overrideServingUrl] A URL to override the recommender's serving URL.
 * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
 * @param {boolean} [options.fetchWithMetadata] Whether to fetch recommendations with metadata.
 * @returns {Promise} Promise which resolves with recommendations.
 * @private
 */
function fetchRecommendationsImpl(recommenderService:RecommenderService, recommender:Recommender, targetId:string, options:any): Promise<any> {
  var fetchWithMetadata = !!(options && options.fetchWithMetadata);
  var servingUrl = (options && options.overrideServingUrl) || recommender.servingUrl;
  // Call end-point.
  return xhr.request({
    url: generateRecommendationsUrl(servingUrl, targetId),
  }).then(function(xhrObj) {
    var recommendations = [];
    var data = getResponseData(xhrObj.response);
    // The data is key-value pairs of recommendations, where the values
    // are themselves JSON encoded objects.
    recommendations = parsePayload(data, recommenderService.serviceVersion);

    if (!fetchWithMetadata || !hasCatalog(recommenderService.serviceVersion)) {
      return recommendations;
    }

    // Has a catalog.
    // The data contains the item ID and score, but not the metadata.
    return fetchItemMetadataImpl(recommenderService, _.map(recommendations, recommenderService.idTagName), options)
      .then(function(recommendationsWithMetadata) {
        recommendationsWithMetadata = _.map(recommendationsWithMetadata, function(r) {
          if (r instanceof Error) {
            r = {} as any;
          }
          // Note(tyler): mitigation for https://nvd.nist.gov/vuln/detail/CVE-2018-3721
          delete r['__proto__'];
          return r;
        });

        // Merge with recommendations to include _score, overriding common field values with those in metadata.
        return _.merge(recommendations, recommendationsWithMetadata);
      });
  });
}

/**
 * Implementor that fetches metadata for a list of items.
 * @param {RecommenderService} recommenderService
 * @param {string|string[]} items Single item id or list of item ids.
 * @param {Object} options
 * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
 * @returns {Promise} Promise which resolves with item metadata(s).
 * @private
 */
function fetchItemMetadataImpl(recommenderService:RecommenderService, items:string|string[], options:any): Promise<any> {
  var catalogUrl = (options && options.overrideCatalogUrl) || recommenderService.catalogUrl;
  if (_.isString(items)) {
    return fetchItemMetadataFromCatalog(catalogUrl, items as string);
  }

  return PromiseEnv.all(_.map(items, function(item) {
    return fetchItemMetadataFromCatalog(catalogUrl, item)['catch'](function(e) {
      Logger.warn('recommender / Failed to get recommendations for item:', item, e);
      return e;
    });
  }));
}


/**
 * Fetches recommendations (with metadata) from recommender.
 * @param {Store} GlobalStore
 * @param {string} targetId
 * @param {Object} options
 * @param {string} [options.recommenderServiceId] ID of recommender service to use.
 * @param {string} [options.recommenderId] ID of recommender to use.
 * @param {string} [options.overrideServingUrl] A URL to override the recommender's serving URL.
 * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
 * @returns {Promise} Promise which resolves with list of recommendations with their metadata.
 * @private
 */
function fetchRecommendations(GlobalStore, targetId, options) {
  var entities = getEntities(GlobalStore, options);
  var recommenderService = entities.recommenderService;
  var recommender = entities.recommender;
  return fetchRecommendationsImpl(recommenderService, recommender, targetId, {
      overrideServingUrl: options && options.overrideServingUrl,
      overrideCatalogUrl: options && options.overrideCatalogUrl,
      fetchWithMetadata: true,
    });
}

/**
 * Fetches recommended items (no metadata except for id and score)  from recommender.
 * @param {Store} GlobalStore
 * @param {string} targetId
 * @param {Object} options
 * @param {string} [options.recommenderServiceId] ID of recommender service to use.
 * @param {string} [options.recommenderId] ID of recommender to use.
 * @param {string} [options.overrideServingUrl] A URL to override the recommender's serving URL.
 * @returns {Promise} Promise which resolves with list of recommended items (id and score).
 * @private
 */
function fetchRecommendedItems(GlobalStore:StoreClass, targetId:string, options:any): Promise<any> {
  var entities = getEntities(GlobalStore, options);
  var recommenderService = entities.recommenderService;
  var recommender = entities.recommender;
  if (!hasCatalog(recommenderService.serviceVersion)) {
    throw new Error('Recommender service ' + recommenderService.id +
      ' serves self-contained recos. Use `fetchRecommendations` API call');
  }

  return fetchRecommendationsImpl(recommenderService, recommender, targetId, {
    overrideServingUrl: options && options.overrideServingUrl,
    fetchWithMetadata: false,
  });
}

/**
 * Fetches metadata for items.
 * @param {Store} GlobalStore
 * @param {string|string[]} items Single item id or list of item ids.
 * @param {Object} options
 * @param {string} [options.recommenderServiceId] ID of recommender service to use.
 * @param {string} [options.recommenderId] ID of recommender to use.
 * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
 * @returns {Promise} Promise which resolves with metadata.
 * @private
 */
function fetchItemMetadata(GlobalStore, items, options) {
  var entities = getEntities(GlobalStore, options);
  var recommenderService = entities.recommenderService;
  if (!hasCatalog(recommenderService.serviceVersion)) {
    throw new Error('Recommender service ' + recommenderService.id +
      ' serves self-contained recos. Use `fetchRecommendations` API call');
  }

  if (_.isArray(items) && items.length > MAX_ITEM_METADATA_TO_FETCH) {
    return PromiseEnv.reject('Cannot fetch more than ' + MAX_ITEM_METADATA_TO_FETCH + ' items');
  }
  return fetchItemMetadataImpl(recommenderService, items, options);
}

/**
 * Creates a recommendations fetcher instance.
 * @returns {RecommendationsFetcher} A new fetcher instance.
 * @private
 */
function getRecommendationsFetcher(GlobalStore, recommenderSettings, targetId, options): any {
  return new RecommendationsFetcher(GlobalStore, recommenderSettings, targetId, options);
}

/**
 * Helper class for fetching recommendations with filtering, and canonicalization.
 * @param {Store} GlobalStore
 * @param {Object} recommenderSettings
 * @param {string} [recommenderSettings.recommenderServiceId] ID of recommender service.
 * @param {string} [recommenderSettings.recommenderId] Id of recommender
 * @param {string} [recommenderSettings.overrideServingUrl] URL to override the recommender's serving URL.
 * @param {string} [recommenderSettings.overrideCatalogUrl] URL to override the recommender service's catalog URL.
 * @param {string} targetId
 * @param {Object} [options]
 * @param {Function} [options.preFilter] Optional function to filter on recommended item ids and score.
 * @param {Function} [options.canonicalize] Optional function to canonicalize recommended item metadata.
 * @param {Function} [options.postFilter] Optional function to filter recommended items with metadata.
 * @constructor
 * @private
 */
function RecommendationsFetcher(GlobalStore:StoreClass, recommenderSettings:any, targetId:string, options?:any) {
  this.recommenderSettings = recommenderSettings;
  var entities = getEntities(GlobalStore, recommenderSettings);
  this.recommenderService = entities.recommenderService;
  this.recommender = entities.recommender;
  this.targetId = targetId;

  if (options) {
    this.preFilter = options.preFilter;
    this.canonicalize = options.canonicalize;
    this.postFilter = options.postFilter;
  }

  /**
   * Global store required to invoke API implementation methods.
   * @type {Store}
   * @private
   */
  this._globalStore = GlobalStore;
  this.reset();
}

/**
 * Reset the recommendation fetcher.
 */
RecommendationsFetcher.prototype.reset = function() {
  this._bufferPromise = null;
};

/**
 * Fetches the next set of n recommendations with their metadata.
 * @param {number} [n] Number of recommendations requested.
 * @returns {Promise} Promise which resolves with list of up to n recommendations with metadata.
 */
RecommendationsFetcher.prototype.next = function(n?:number): Promise<any> {
  var numNeeded = Math.floor(!_.isNumber(n) ? 1 : n);
  if (numNeeded <= 0) {
    throw new Error('Invalid argument ' + n + '. Must be positive integer');
  }

  if (!this._bufferPromise) {
    this._bufferPromise = fetchAndBuffer.call(this);
  }

  // Extract required number of recommendations from buffer, fetching more if required.
  // Also, update state of buffer.
  var resultAndBufferPromise = this._bufferPromise.then(_.bind(function(buffer) {
    var recosSoFar = [];
    var numAvailable = buffer.recos.length - buffer.recosOffset;

    if (numAvailable > 0) {
      recosSoFar = buffer.recos.slice(buffer.recosOffset, buffer.recosOffset + numNeeded);
      buffer.recosOffset += recosSoFar.length;
      numNeeded -= recosSoFar.length;
    }

    if (numNeeded <= 0) {
      return {
        result: recosSoFar,
        buffer: buffer
      };
    }

    // More recos needed; fetch 'em.
    return fetchMeta.call(this, {
        buffer: buffer,
        recosSoFar: recosSoFar,
        numNeeded: numNeeded
      })
      .then(function(x) {
        return {
          result: x.recosSoFar,
          buffer: x.buffer
        };
      });
  }, this));

  this._bufferPromise = resultAndBufferPromise.then(function(x) { return x.buffer; });

  return resultAndBufferPromise.then(function(x) { return x.result; });
};

/**
 * Fetch recommended items and buffer. These items may already contain metadata
 * if the recommender service serves self-contained recommendations. Otherwise,
 * the metadata will be fetched on-demand by `fetchMeta`.
 * @returns {Promise} Promise that resolves with buffer recommended items (with
 * metadata for self-contained reco service).
 * @private
 */
function fetchAndBuffer(): Promise<any> {
  var buffer = {
    recosNoMeta: [],
    recosNoMetaOffset: 0,
    recos: [],
    recosOffset: 0,
  };

  Logger.debug('recommender / fetching recommended items for', this.targetId, 'from recommender', this.recommender.id);

  if (hasCatalog(this.recommenderService.serviceVersion)) {
    return fetchRecommendedItems(this._globalStore, this.targetId, this.recommenderSettings)
      .then(_.bind(function(result) {
        buffer.recosNoMeta = _.filter(result, this.preFilter);
        return buffer;
      }, this));
  } else {
    return fetchRecommendations(this._globalStore, this.targetId, this.recommenderSettings)
      .then(_.bind(function(result) {
        // Self-contained recommendations already have metadata; apply all the things.
        buffer.recos = _(result)
          .filter(this.preFilter)
          .map(this.canonicalize)
          .filter(this.postFilter)
          .value();
        return buffer;
      }, this));
  }
}

/**
 * Fetch recommended item metadata on-demand.
 * This function is recursive in that it re-enqueues itself if more recos are needed at the end.
 * @param {Object} recurseState Current state of buffer, recommendations with metadata,
 * and number of recommendations still required.
 * @returns {Promise}
 * @private
 */
function fetchMeta(recurseState:any): Promise<any> {
  // First check if we need more recos, and if we still have any recosNoMeta available to fetch meta for.
  var numNeeded = recurseState.numNeeded;
  var buffer = recurseState.buffer;
  var numAvailable = buffer.recosNoMeta.length - buffer.recosNoMetaOffset;
  if (numNeeded <= 0 || numAvailable <= 0) {
    // The terminal condition is we have either fetched all requested recos or run out of recosNoMeta.
    // In either case we'll return what have been fetched so far in recurseState.recosSoFar.
    return PromiseEnv.resolve(recurseState);
  }

  var minMetaBatchSize = 5;
  var batchSize = Math.max(numNeeded, minMetaBatchSize);

  // Get ids of recommended items to fetch metadata for.
  var recosToFetch = _.slice(buffer.recosNoMeta, buffer.recosNoMetaOffset, buffer.recosNoMetaOffset + batchSize);
  var recoIds = _.map(recosToFetch, this.recommenderService.idTagName);
  buffer.recosNoMetaOffset += recoIds.length;

  var recosSoFar = recurseState.recosSoFar;
  Logger.debug('recommender / fetching metadata for', recoIds, 'from recommender', this.recommender.id);

  // Go ahead and fetch meta for recoIds.
  return fetchItemMetadata(this._globalStore, recoIds, this.recommenderSettings)
    .then(_.bind(function(metas) {
      // Convert errors to empty meta {}.
      metas = _.map(metas, function(r) { return (r instanceof Error) ? {} : r; });

      // Store this new batch in buffer.
      buffer.recos = _(recosToFetch)
        .merge(metas) // Merge metas into recosToFetch, overriding common field values with those in metas.
        .map(this.canonicalize)
        .filter(this.postFilter)
        .value();

      // We may have fetched more than what's requested due to minMetaBatchSize.
      // Takes what's requested and keep the rest in buffer.
      var used = _.slice(buffer.recos, 0, numNeeded);
      buffer.recosOffset = used.length;

      recurseState.recosSoFar = recosSoFar.concat(used);
      recurseState.numNeeded -= used.length;

      // We may also have fetched fewer than what's requested due to errors.
      // Recurse into fetchMeta again to try to fetch more.
      if (recurseState.numNeeded > 0) {
        return fetchMeta.call(this, recurseState);
      }

      // All done. Return the final state.
      return recurseState;
    }, this));
}

module.exports = [
  'stores/global',
  function ApiFactory(GlobalStore) {
    return {
      /**
       * Fetches recommendations (with metadata) from recommender.
       * @param {string} targetId
       * @param {Object} options
       * @param {string} [options.recommenderServiceId] ID of recommender service.
       * @param {string} [options.recommenderId] Id of recommender
       * @param {string} [options.overrideServingUrl] A URL to override the recommender's serving URL.
       * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
       * @returns {Promise} Promise which resolves with list of recommendations containing metadata.
       */
      fetchRecommendations: _.partial(fetchRecommendations, GlobalStore),
      /**
       * Fetches recommended items (without item metadata) for a given item.
       * @param {string} targetId
       * @param {Object} options
       * @param {string} [options.recommenderServiceId] ID of recommender service.
       * @param {string} [options.recommenderId] Id of recommender
       * @param {string} [options.overrideServingUrl] A URL to override the recommender's serving URL.
       * @returns {Promise} Promise which resolves with list of recommended items containing id and score.
       */
      fetchRecommendedItems: _.partial(fetchRecommendedItems, GlobalStore),
      /**
       * Fetches metadata for items (max 20).
       * @param {string|string[]} items Single item id or list of item ids.
       * @param {Object} options
       * @param {string} [options.recommenderServiceId] ID of recommender service.
       * @param {string} [options.recommenderId] Id of recommender
       * @param {string} [options.overrideCatalogUrl] A URL to override the recommender service's catalog URL.
       * @returns {Promise} Promise which resolves with item metadata or Error object for failed items.
       */
      fetchItemMetadata: _.partial(fetchItemMetadata, GlobalStore),
      /**
       * Creates an iterator-like helper for fetching recommendations with filtering and canonicalization.
       * This is a high-level API that handles both v1 and v2 recommender services.
       *
       * Example usage:
       * ```javascript
       * var fetcher = optimizely.get('recommender').getRecommendationsFetcher(...);
       * var recosPromise = fetcher.next(2);
       * var moreRecosPromise = fetcher.next(5);
       * fetcher.reset();
       * ```
       *
       * Internally, the sequence of operations (for v2 recommender services) are:
       * - Invoke `fetchRecommendedItems` to fetch recommended items without metadata.
       * - Invoke `options.preFilter` to filter on recommended items' id and score.
       * - Invoke `fetchItemMetadata` to fill recommendations with metadata.
       * - Invoke `options.canonicalize` to canonicalize recommendations.
       * - Invoke `options.postFilter` to filter on canonicalized recommendations.
       *
       * The efficiency comes from the fact that we are fetching recommendations metadata only as needed.
       *
       * @param {Object} recommenderSettings
       * @param {string} [recommenderSettings.recommenderServiceId] ID of recommender service.
       * @param {string} [recommenderSettings.recommenderId] Id of recommender
       * @param {string} [recommenderSettings.overrideServingUrl] URL to override the recommender's serving URL.
       * @param {string} [recommenderSettings.overrideCatalogUrl] URL to override the recommender service's catalog URL.
       * @param {string} targetId
       * @param {Object} [options]
       * @param {Function} [options.preFilter] Function to filter on recommended item ids and score.
       * @param {Function} [options.canonicalize] Function to canonicalize recommended item metadata.
       * @param {Function} [options.postFilter] Function to filter recommended items with metadata.
       */
      getRecommendationsFetcher: _.partial(getRecommendationsFetcher, GlobalStore),
    };
  },
];
